import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, ArrowLeft, RotateCcw, CheckCircle2, XCircle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchScrambleDaily, submitScramble } from '../../services/gamesApi';
import GameResultCard from './GameResultCard';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Verse Scramble — given a daily verse with words randomized, the player
 * taps words in the correct order. Tapping a placed word returns it to the
 * pool. Submission validates against the original verse text.
 *
 * Tap-based reordering (not drag-drop) keeps the game touch-friendly on
 * mobile without pulling in a drag library.
 */
const VerseScrambleGame = ({ language = 'en', onBack, onScored }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pool, setPool] = useState([]);          // [{id, word}]
  const [selected, setSelected] = useState([]);  // [{id, word}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchScrambleDaily(language)
      .then((d) => {
        setData(d);
        // Stamp each shuffled word with a stable id so duplicates ("a", "and")
        // can be tracked individually as the user picks them.
        setPool(d.shuffled.map((w, i) => ({ id: i, word: w })));
        setSelected([]);
      })
      .catch(() => toast.error(t('games.failedLoad')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const pick = (item) => {
    setPool((p) => p.filter((x) => x.id !== item.id));
    setSelected((s) => [...s, item]);
  };
  const unpick = (item) => {
    setSelected((s) => s.filter((x) => x.id !== item.id));
    setPool((p) => [...p, item]);
  };
  const reset = () => {
    if (!data) return;
    setPool(data.shuffled.map((w, i) => ({ id: i, word: w })));
    setSelected([]);
  };

  const onSubmit = async () => {
    if (!data) return;
    if (selected.length === 0) {
      toast.error(t('games.scramble.buildFirst'));
      return;
    }
    setSubmitting(true);
    try {
      const r = await submitScramble(data.id, selected.map((s) => s.word));
      setResult(r);
      onScored?.(r);
    } catch {
      toast.error(t('games.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('games.loading')}
      </div>
    );
  }
  if (!data) return null;

  if (result) {
    return (
      <div className="space-y-4" data-testid="scramble-result-screen">
        <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('games.back')}
        </Button>
        <GameResultCard
          language={language}
          gameTitle={t('games.scramble.title')}
          correct={result.correct ? 1 : 0}
          total={1}
          score={result.score}
          perfect={result.correct}
          streak={result.streak}
          newTotal={result.new_total}
          subtitle={data.reference}
        />
        <Card>
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {result.correct
                ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                : <XCircle className="w-5 h-5 text-red-500" />}
              <p className="font-semibold">
                {result.correct
                  ? t('games.scramble.correct')
                  : t('games.scramble.notQuite')}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('games.scramble.correctVerse')}
            </p>
            <p className="italic text-foreground">{result.expected.join(' ')}</p>
            <p className="text-[10px] text-muted-foreground">{data.reference} · {data.translation}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="scramble-game-screen">
      <Button onClick={onBack} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('games.back')}
      </Button>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">
          {t('games.scramble.title')}
        </h2>
        <Badge variant="outline" className="text-[10px]">
          {data.reference} · {data.translation}
        </Badge>
      </div>

      <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-slate-800 dark:to-slate-700 border-purple-200/50 dark:border-purple-700/50">
        <CardContent className="p-4 min-h-[80px]">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {t('games.scramble.yourVerse')}
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="scramble-selected-pane">
            {selected.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                {t('games.scramble.tapToBuild')}
              </p>
            )}
            {selected.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => unpick(it)}
                className="px-2.5 py-1 rounded-md bg-white dark:bg-slate-900 border border-purple-300/60 dark:border-purple-600/60 text-sm hover:bg-purple-50 dark:hover:bg-purple-900/30"
                data-testid={`scramble-selected-${it.id}`}
              >
                {it.word}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
            {t('games.scramble.availableWords')}
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="scramble-pool-pane">
            {pool.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => pick(it)}
                className="px-2.5 py-1 rounded-md bg-muted/50 border border-border text-sm hover:border-primary/50 hover:bg-muted"
                data-testid={`scramble-pool-${it.id}`}
              >
                {it.word}
              </button>
            ))}
            {pool.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                {t('games.scramble.readyToSubmit')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={reset} variant="outline" size="sm" data-testid="scramble-reset-btn">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          {t('games.reset')}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting || selected.length === 0}
          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
          data-testid="scramble-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {t('games.submit')}
        </Button>
      </div>
    </div>
  );
};

export default VerseScrambleGame;
